// src/components/MyProfileModal.tsx
//
// BekalNanti — Milestone P3.6: My Profile Modal
// Allows the logged-in user to manage and update their account profile safely.

import React, { useState, useEffect } from 'react'
import { ProfileRecord } from '../journey/types'
import { updateUserProfile } from '../lib/peopleService'
import { X, User, Check, AlertCircle, Loader2, Mail, Globe, Coins } from 'lucide-react'

interface MyProfileModalProps {
  isOpen: boolean
  profile: ProfileRecord
  userEmail?: string
  onClose: () => void
  onUpdateSuccess: (updated: ProfileRecord) => void
}

export const MyProfileModal: React.FC<MyProfileModalProps> = ({
  isOpen,
  profile,
  userEmail,
  onClose,
  onUpdateSuccess,
}) => {
  const [displayName, setDisplayName] = useState(profile.displayName || '')
  const [preferredLanguage, setPreferredLanguage] = useState(profile.preferredLanguage || 'id')
  const [defaultCurrency, setDefaultCurrency] = useState(profile.defaultCurrency || 'IDR')
  const [loading, setLoading] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [successMsg, setSuccessMsg] = useState<string | null>(null)

  useEffect(() => {
    setDisplayName(profile.displayName || '')
    setPreferredLanguage(profile.preferredLanguage || 'id')
    setDefaultCurrency(profile.defaultCurrency || 'IDR')
    setErrorMsg(null)
    setSuccessMsg(null)
  }, [profile, isOpen])

  if (!isOpen) return null

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = displayName.trim()
    if (!trimmed) {
      setErrorMsg('Nama tampilan tidak boleh kosong.')
      return
    }

    setLoading(true)
    setErrorMsg(null)
    setSuccessMsg(null)

    try {
      const updated = await updateUserProfile(profile.id, {
        displayName: trimmed,
        preferredLanguage,
        defaultCurrency,
      })
      setSuccessMsg('Profil berhasil diperbarui!')
      onUpdateSuccess(updated)
      setTimeout(() => {
        onClose()
      }, 1000)
    } catch (err: any) {
      setErrorMsg(err.message || 'Gagal menyimpan perubahan profil.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs animate-in fade-in duration-200"
      role="dialog"
      aria-modal="true"
      aria-labelledby="my-profile-modal-title"
    >
      <div className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl border border-slate-200/90 overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-teal-50 text-[#0EA5A4] flex items-center justify-center">
              <User size={18} />
            </div>
            <div>
              <h2 id="my-profile-modal-title" className="text-base font-bold text-slate-900 leading-tight">
                Profil Pengelola
              </h2>
              <p className="text-[11px] text-slate-500">
                Pengaturan akun pembuat rencana BekalNanti
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer"
            aria-label="Tutup"
          >
            <X size={18} />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSave} className="p-5 overflow-y-auto space-y-4 text-left">
          {errorMsg && (
            <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-xs flex items-center gap-2">
              <AlertCircle size={14} className="flex-shrink-0" />
              <span>{errorMsg}</span>
            </div>
          )}
          {successMsg && (
            <div className="p-3 rounded-xl bg-teal-50 border border-teal-200 text-[#0F766E] text-xs flex items-center gap-2">
              <Check size={14} className="flex-shrink-0" />
              <span>{successMsg}</span>
            </div>
          )}

          {/* Email (Read Only) */}
          {userEmail && (
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                <Mail size={13} className="text-slate-400" />
                <span>Email Akun</span>
              </label>
              <div className="px-3 py-2.5 rounded-xl border border-slate-200/80 bg-slate-50 text-xs sm:text-sm text-slate-600 font-medium select-all">
                {userEmail}
              </div>
              <p className="text-[11px] text-slate-400">
                Email terverifikasi terhubung dengan sesi login Supabase Anda.
              </p>
            </div>
          )}

          {/* Display Name */}
          <div className="space-y-1.5">
            <label htmlFor="display-name-input" className="text-xs font-bold text-slate-700">
              Nama Tampilan Pengelola
            </label>
            <input
              id="display-name-input"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Nama lengkap atau panggilan Anda"
              required
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-white text-xs sm:text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#0EA5A4]"
            />
            <p className="text-[11px] text-slate-400">
              Nama ini akan tampil di ruang perencanaan keluarga Anda.
            </p>
          </div>

          {/* Preferred Language */}
          <div className="space-y-1.5">
            <label htmlFor="pref-lang-select" className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
              <Globe size={13} className="text-slate-400" />
              <span>Bahasa Antarmuka</span>
            </label>
            <select
              id="pref-lang-select"
              value={preferredLanguage}
              onChange={(e) => setPreferredLanguage(e.target.value)}
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-white text-xs sm:text-sm font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#0EA5A4]"
            >
              <option value="id">Bahasa Indonesia</option>
              <option value="en">English (US)</option>
            </select>
          </div>

          {/* Default Currency */}
          <div className="space-y-1.5">
            <label htmlFor="default-curr-select" className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
              <Coins size={13} className="text-slate-400" />
              <span>Mata Uang Utama</span>
            </label>
            <select
              id="default-curr-select"
              value={defaultCurrency}
              onChange={(e) => setDefaultCurrency(e.target.value)}
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-white text-xs sm:text-sm font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#0EA5A4]"
            >
              <option value="IDR">IDR (Rupiah Indonesia - Rp)</option>
              <option value="USD">USD (US Dollar - $)</option>
            </select>
          </div>

          {/* Action Buttons */}
          <div className="pt-3 border-t border-slate-100 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 text-xs font-semibold cursor-pointer"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white text-xs font-bold shadow-2xs transition-all cursor-pointer disabled:opacity-50"
            >
              {loading && <Loader2 size={13} className="animate-spin" />}
              <span>Simpan Perubahan</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
